// Rezervasyon API'sinde karşılığı olmayan ama API verisinden türetilen sınıflandırmalar.
// Bunlar editoryal kurallardır; API değişirse burası güncellenir. Ayrıntı: docs/api-integration.md

export type RegionSlug = "avrupa" | "asya" | "afrika" | "amerika" | "kruvaziyer";

// Bölgenin görünen adı, sayfa başlığı ve tanıtım metni Site Yönetimi → Bölgeler ve destinasyonlar'dan
// (features/site-content); buradaki ad yalnızca yerleşik Türkçe varsayılan. Bölge listesi, adres ve eşleme burada kalır.
export type Region = {
  slug: RegionSlug;
  name: string;
  /** Mevcut sitedeki adres korunur (flyexpress.com/avrupa-turlari ...) */
  path: string;
  /** API kategori kimlikleri (GetCategories / Search category.id) */
  categoryIds: string[];
};

export const regions: Region[] = [
  {
    slug: "avrupa",
    name: "Avrupa",
    path: "/avrupa-turlari",
    categoryIds: [
      "16", "27", "28", "29", "30", "31", "32", "34", "35", "36", "38", "39", "40", "43", "48", "49",
      "10060", "10061", "10063", "10064", "10075", "10083", "10085", "10086", "10089", "10090", "10092",
      "10094", "10095", "10096", "10101", "10115", "10127", "10130", "10131", "10140", "10149", "10152",
    ],
  },
  {
    slug: "asya",
    name: "Asya",
    path: "/asya-turlari",
    categoryIds: [
      "15", "21", "22", "23", "24", "26", "10065", "10098", "10105", "10108", "10120", "10125", "10132",
      "10137", "10142", "10144", "10146",
    ],
  },
  {
    slug: "afrika",
    name: "Afrika",
    path: "/afrika-turlari",
    categoryIds: ["2", "12", "14", "10119", "10134", "10138"],
  },
  {
    slug: "amerika",
    name: "Amerika",
    path: "/amerika-turlari",
    categoryIds: ["17", "41", "10056", "10136"],
  },
  {
    slug: "kruvaziyer",
    name: "Kruvaziyer",
    path: "/kruvaziyer-turlari",
    categoryIds: ["10114"],
  },
];

/**
 * API kategori adı büyük harf veya kısaltma olduğunda gösterilecek ad. Destinasyon sayfasının adresi bu addan üretilir
 * (değişmez); görünen ad Site Yönetimi → Bölgeler ve destinasyonlar → Destinasyon adları'ndan değiştirilebilir
 * (features/translations/tours.ts → getCatalogFor). Buradaki adlar yerleşik Türkçe varsayılandır.
 */
export const categoryLabels: Record<string, string> = {
  "2": "Afrika Turları",
  "15": "Asya Turları",
  "16": "Avrupa Turları",
  "17": "Amerika Turları",
  "10064": "İspanya, Fransa & İtalya Turları",
  "10065": "Vietnam, Kamboçya & Tayland Turları",
  "10086": "Fransa",
  "10094": "Rusya",
  "10096": "St. Petersburg",
  "10098": "Gürcistan Turları",
  "10108": "Azerbaycan Turları",
  "10109": "Azerbaycan Maç Turları",
  "10114": "Gemi Turları",
};

export type FamilySlug = "fly-cruise" | "fly-exclusive" | "fly-elegant" | "fly-classic";

// Görünen ad, logo, görseller ve metinler Site Yönetimi → Ürün aileleri'nden (features/site-content); buradaki ad, logo
// ve metinler yalnızca yerleşik varsayılan. Adres (slug) ve tur üyeliği kuralı burada kalır.
export type Family = {
  slug: FamilySlug;
  name: string;
  short: string;
  /** Mevcut sitedeki ürün ailesi logosu (public/brand/families); panelde logo seçilmemişse bu dosya */
  logo: string;
  /** flyexpress.com ürün sayfasındaki metin (iddialar 2026-10-05'te ürün sahibince onaylandı) */
  description: string[];
  /** Türetme kuralı: tur adı veya kategori. Sıra önemlidir; ilk eşleşen kazanır. */
  rule: { nameIncludes?: RegExp; categoryIds?: string[] };
  ruleText: string;
};

export const families: Family[] = [
  {
    slug: "fly-cruise",
    name: "Fly Cruise",
    short: "Avantajlı hizmetler ve uygun uçak fiyatlarıyla hazırlanmış kruvaziyer paketleri.",
    logo: "/brand/families/fly-cruise.jpg",
    description: [
      "Süregelen tecrübemizle ürün portföyümüze kattığımız Fly Cruise programları, avantajlı hizmetlerin ve uygun uçak fiyatlarının özenle araştırılıp eklendiği paketler halinde satışa sunuluyor.",
      "Talep gören turlar için ayrıca Türkçe rehberlik hizmeti de eklenebilen programda dünyanın dört bir yanını keşfedebileceğiniz turlar mevcut. Yine talebe göre kimi programların öncesine ve sonrasına farklı konaklama seçenekleri eklenmesi ve böylelikle kara turlarının özelleştirilmesi mümkün.",
      "Fly Cruise, henüz ilk senesinde Avrupa'da en çok satış yapan ilk üç acenteden biri olmayı başardı.",
    ],
    rule: { categoryIds: ["10114"] },
    ruleText: "Rezervasyon sisteminde “Gemi Turları” kategorisindeki turlar",
  },
  {
    slug: "fly-exclusive",
    name: "Fly Exclusive",
    short: "Doğru zamanda doğru yerde olmanızı sağlayan, en fazla yirmi kişilik turlar.",
    logo: "/brand/families/fly-exclusive.jpg",
    description: [
      "Bazı mekânları sizin için unutulmaz kılan, “tam zamanında” orada olmaktır. Her şehrin güzelliğinin doruğuna ulaştığı bir mevsim vardır. Fly Exclusive programlar, tam da bu düşünceden yola çıkarak “doğru zamanda doğru yerde” olmanızı sağlayacak turlardan oluşuyor.",
      "Maksimum yirmi kişilik kapasiteli, alanında uzman rehberlerin eşliğinde planlanan turlar sayesinde, ziyaretleriniz bir görsel şölen olmanın ötesine geçerek mekânın ruhunu anlamanızı kolaylaştırıyor. Fly Exclusive bünyesindeki turlarımızda mümkün olan her bölgeye direkt seferlerle uçuluyor ve konuklarımız merkezi bölgelerde yer alan 4 ve 5 yıldızlı otellerde ağırlanıyor.",
    ],
    rule: { nameIncludes: /\bEXCLUSIVE\b/i },
    ruleText: "Tur adında “Exclusive” geçen turlar",
  },
  {
    slug: "fly-elegant",
    name: "Fly Elegant",
    short: "Bütün ekstra turların programa dahil olduğu, sene boyu süren turlar.",
    logo: "/brand/families/fly-elegant.jpg",
    description: [
      "Hangi ekstra tura katılmak, hangisini pas geçmek istediğine karar veremeyenlerden misiniz? O zaman sizi bütün ekstra turların programa dahil olduğu Fly Elegant programımıza alalım.",
      "Konuklarımızı 3 ve 4 yıldızlı otellerde ağırladığımız ve sene boyunca devam eden turlarımızla dünyanın dört bir yanına kolayca uçabilirsiniz. Müze ve ören yeri girişlerinin katılımcının inisiyatifinde olduğu 100'den fazla seçenekle Elegant turlarımız, tamamen hayallerinizi keyifli anılara dönüştürmek için planlandı.",
    ],
    rule: { nameIncludes: /\bELEGANT\b/i, categoryIds: ["32"] },
    ruleText: "Tur adında “Elegant” geçen veya “Elegant Avrupa Turları” kategorisindeki turlar",
  },
  {
    slug: "fly-classic",
    name: "Fly Classic",
    short: "Hayallerinizle bütçeniz arasında uzlaşma sağlayan ekonomik paketler.",
    logo: "/brand/families/fly-classic.jpg",
    description: [
      "Avrupa hayalleriniz Edirne'nin, Keşan'ın ötesine geçsin diye hayallerinizle bütçeniz arasında bir uzlaşma sağlayan ekonomik tur paketlerinin yer aldığı bu program, sene boyunca elinizin altında.",
      "26 yıllık tecrübeyle kusursuz işleyen ve sene boyunca hemen hemen her hafta bir alternatif bulabileceğiniz turlarımızda ekstra turlar opsiyonel olarak sunuluyor. Dilerseniz daha fazla yer keşfedebilir ya da bir kahve eşliğinde şehrin tadını çıkarabilirsiniz. Bu paketteki programların %90'ını Avrupa şehirleri oluştururken konuklarımızı şehrin kalabalığından ve gürültüsünden uzakta yer alan 3 ve 4 yıldızlı otellerde ağırlıyoruz.",
    ],
    rule: { nameIncludes: /FLYEXCLASSIC|\bCLASSIC\b/i },
    ruleText: "Tur adında “Classic” veya “FlyExClassic” geçen turlar",
  },
];

/** Kalkış havalimanı kodu → gösterim adı. Listede olmayan kod API'deki haliyle gösterilir. */
export const airports: Record<string, { city: string; name: string }> = {
  IST: { city: "İstanbul", name: "İstanbul Havalimanı" },
  SAW: { city: "İstanbul", name: "Sabiha Gökçen Havalimanı" },
  ESB: { city: "Ankara", name: "Esenboğa Havalimanı" },
  ADB: { city: "İzmir", name: "Adnan Menderes Havalimanı" },
};

/** IATA havayolu kodu → gösterim adı. API "TK - T.H.Y" biçiminde kısaltma veriyor. */
export const airlines: Record<string, string> = {
  TK: "Türk Hava Yolları",
  PC: "Pegasus",
  VF: "AJet",
  XQ: "SunExpress",
  QR: "Qatar Airways",
  EK: "Emirates",
  SV: "Saudia",
  G9: "Air Arabia",
  H7: "HiSky",
  NH: "All Nippon Airways",
  KM: "KM Malta Airlines",
  D7: "AirAsia X",
  LO: "LOT Polish Airlines",
};

/** Tur listesi için arama aralığı (bugünden itibaren ay). Search tek istekte tüm kataloğu döndürür. */
export const CATALOG_MONTHS_AHEAD = 18;
export const PAGE_SIZE = 12;
